import { Inject, Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, combineLatest, map, take } from 'rxjs';
import { ActiveSecurityContextStateService } from '@security/active-security/active-security-context.state-service';
import { ActiveCintraProductService } from '@app/active-cintra-product.service';
import { HeaderConfig, AccountMenuItem } from '../../../component-library/layout';
import { AuthenticationService } from '@auth-n/authentication.service';
import { FeatureFlagService, FeatureFlagsServiceInjectionToken } from '@feature-flags/feature-flag.service';
import { BeamerService } from '@app/app-layout/header/beamer.service';
import { HelpLinkType, HelpLinksService } from '@app/help-links/help-links.service';
import { emailPreferencesRoutes } from '@app/email-preferences/email-preferences-api-routes';
import { withoutHiddenPasskeySettings } from '@domain/passkey-settings-visibility';

@Injectable({
  providedIn: 'root'
})
export class HeaderAdapterService {
  private readonly helpLinks = inject(HelpLinksService);

  constructor(
    private activeSecurity: ActiveSecurityContextStateService,
    private activeCintraProductService: ActiveCintraProductService,
    private router: Router,
    private authenticationService: AuthenticationService,
    private beamerService: BeamerService,
    @Inject(FeatureFlagsServiceInjectionToken) private featureFlagService: FeatureFlagService
  ) {}

  getHeaderConfig$(): Observable<HeaderConfig> {
    return combineLatest([
      this.activeSecurity.activeMembership$,
      this.activeCintraProductService.activeProduct$,
      this.featureFlagService.getAllValues$(),
      this.beamerService.unreadCount$
    ]).pipe(
      map(([activeMembership, activeProduct, featureFlags, unreadCount]) => {
        const activeAreas = this.activeSecurity.activeAreas;
        const activeUser = this.activeSecurity.activeUser;
        const displayName = this.activeSecurity.activeMembership?.iqPersonDisplayName;

        return {
          activeAreas,
          activeUser: {
            surname: activeUser.surname,
            forename: activeUser.forename,
            displayName: displayName,
            avatarUrl: activeMembership.avatarUrl
          },
          activeProduct,
          // Rebuilt on every getAllValues$ emission, so a flag that lands after bootstrap still takes effect.
          accountMenuItems: withoutHiddenPasskeySettings(
            this.convertUserAccountLinksToMenuItems(activeAreas?.userAccountModel?.userAccountLinks || [], featureFlags),
            featureFlags
          ),
          beamerLink: BeamerService.hasBeamer(activeProduct)
            ? {
                title: "What's new?",
                count: unreadCount
              }
            : undefined,
          helpLink: {
            url: this.helpLinks.getUrl(HelpLinkType.Home)
          },
          featureFlags,
          canAccessPayrolls: this.activeSecurity.hasOrganisationAuthorityTo('AccessPayrolls'),
          organisationLogoUrl: activeMembership.organisationLogoUrl
        };
      })
    );
  }

  private convertUserAccountLinksToMenuItems(userAccountLinks: any[], featureFlags: any): AccountMenuItem[] {
    const menuItems: AccountMenuItem[] = [];

    // Convert user account links
    userAccountLinks.forEach((link) => {
      if (link.label === 'Sign Out') {
        // Add Cookie Preferences before Sign Out (matching legacy logic)
        menuItems.push({
          label: 'Cookie Preferences',
          execute: () => {
            // Trigger the cookie preferences modal
            const cookiePreferenceSsettingsButton = document.querySelector('[data-cky-tag="settings-button"]') as HTMLElement;
            if (cookiePreferenceSsettingsButton) {
              cookiePreferenceSsettingsButton.click();
            }
          }
        });

        // Email Notification Preferences sits between Cookie Preferences and Sign Out, per the design.
        //
        // `path` only, no `execute`: convertToMenuItem checks `execute` first, so supplying both would silently skip
        // the modal handling and this would navigate to a page that does not exist.
        //
        // Compared against `true` rather than checked for truthiness because FeatureFlagType includes string and
        // number.
        if (featureFlags?.[emailPreferencesRoutes.featureFlagKey] === true && this.activeSecurity.canManageEmailPreferences()) {
          menuItems.push({
            label: 'Email Notification Preferences',
            path: `(modal:${emailPreferencesRoutes.modalOutletSegment})`,
            tid: 'email-notification-preferences-menu-item'
          });
        }

        // Add Sign Out
        menuItems.push({
          label: link.label,
          execute: () => this.authenticationService.signOutAndRedirect()
        });
      } else {
        // Check if this is a modal route - if so, use path only
        if (link.path && link.path.startsWith('(modal:')) {
          menuItems.push({
            label: link.label,
            path: link.path,
            disabled: link.disabled,
            tooltip: link.tooltip
          });
        } else {
          // Regular route - use execute function for navigation
          menuItems.push({
            label: link.label,
            execute: () => {
              this.router.navigateByUrl(link.path).then();
            },
            disabled: link.disabled,
            tooltip: link.tooltip
          });
        }
      }
    });

    // Add impersonation end if needed
    if (this.activeSecurity.isImpersonated()) {
      menuItems.push({
        label: 'End Impersonation',
        execute: () => this.activeSecurity.endUserImpersonation()
      });
    }

    return menuItems;
  }
}
